import {addPlayersToTeam} from '@/api/api';
import {FKOPages} from '@/common/fko-utils';
import SessionExpectationContent from '@/components/Programs/SessionExpectationContent';
import SessionPreparationContent from '@/components/Programs/SessionPreparationContent';
import {getLocationBySiteId} from '@/constants/locations';
import {useEffect, useRef} from 'react';
import {Helmet} from 'react-helmet';
import {useNavigate} from 'react-router-dom';
import imageAction from '../../../assets/img/confirmation-action-800.jpg';
import FiveStars from '../../../assets/img/icons/redesign-icons/5-star-rating.svg?react';
import imageTraining from '../../../assets/img/training-2.jpeg';
import {logAnalyticsEvent} from '../../../common/analytics-events';
import {useGetSport} from '../../../common/useGetSport';
import {FormHeading} from '../../../components/Forms/FormHeading/FormHeading';
import {FormSectionHeading} from '../../../components/Forms/FormSectionHeading/FormSectionHeading';
import {useAppDispatch, useAppSelector} from '../../../redux/reduxUtils';
import {FkoPlayer, setFkoCurrentPage} from '../../../redux/slices/fkoFormData';
import {formatPhoneNumber} from '../../../utils/utils';
import {ConfirmationCard} from '../../RegistrationScheduling/ConfirmationCard';
import {Session} from '../../RegistrationScheduling/SchedulingForm';
import FkoEndOfPageMessage from './components/FkoEndOfPageMessage';
import FkoSubHeading from './components/FkoSubHeading';
import FkoVideoEmbed from './components/FkoVideoEmbed';
import styles from './FkoConfirmationPage.module.scss';

type ScheduledPlayer = FkoPlayer & {
  selectedSession: Session;
};

export const FkoConfirmationPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {playersArray, userData, initialParams, siteId} = useAppSelector((state) => state.fkoFormDataReducer);
  const confirmationEventFired = useRef(false);
  const {sessionTypeName, basePath, isFreeBaselineSession, isDbat, isPlayerAssessment, fkoAnalyticsPrefix} =
    useGetSport();

  const location = siteId ? getLocationBySiteId(siteId) : undefined;
  const locationName = location?.name;

  useEffect(() => {
    if (!playersArray?.length) {
      navigate(basePath, {replace: true});
      return;
    }

    if (!confirmationEventFired.current) {
      confirmationEventFired.current = true;
      const scheduledPlayers = playersArray.filter((p) => p.selectedSession);
      const email = userData?.email ?? playersArray.find((p) => p.email)?.email;
      const phone = userData?.phoneNumber ?? playersArray.find((p) => p.phoneNumber)?.phoneNumber;
      if (scheduledPlayers.length) {
        logAnalyticsEvent(`${fkoAnalyticsPrefix}_confirmation_page_view`, {
          playerIds: scheduledPlayers.map((player) => player.mboDetails?.clientId).join(','),
          profileIds: scheduledPlayers.map((player) => player._id).join(','),
          locationName: locationName || scheduledPlayers[0].selectedSession?.location?.name,
          email,
          phone,
        });
      }
    }
  }, [navigate, basePath, playersArray, fkoAnalyticsPrefix, locationName]);

  useEffect(() => {
    dispatch(setFkoCurrentPage(FKOPages.PLAYERS));
    return () => {
      dispatch(setFkoCurrentPage(''));
    };
  }, [dispatch]);

  if (!playersArray?.length) {
    return null;
  }

  const players: ScheduledPlayer[] = [];

  playersArray.forEach((player) => {
    if (player.selectedSession) {
      players.push(player as ScheduledPlayer);
    }
  });

  if (isPlayerAssessment && initialParams?.teamId && siteId) {
    const playersToBeAdded = players?.map((player) => player?.mboDetails?.clientId || '') || [];
    void addPlayersToTeam({
      teamId: initialParams.teamId,
      clientIds: playersToBeAdded,
      siteId,
    });
  }

  // Location details returned with the available session info. This is not the same info as we have in our locations.ts file.
  const mboLocationDetails = players[0].selectedSession.location;
  const mboLocationName =
    mboLocationDetails.name === 'D-BAT Burnsville' ? 'TOCA Soccer Center Burnsville' : mboLocationDetails.name;
  const address = `${mboLocationDetails.address} ${mboLocationDetails.address2}`;
  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GMAPS_API_KEY}&q=${encodeURI(
    address
  )}`;

  let pageTitle = `MyTOCA | ${sessionTypeName} Confirmation`;
  let formHeading = `You’re all set for your upcoming ${sessionTypeName}!`;
  if (isFreeBaselineSession) {
    formHeading = `GOAAAL! Your TOCA Baseline Session is confirmed.`;
  }

  if (isDbat) {
    pageTitle = `D-Bat | ${sessionTypeName} Confirmation`;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className={styles.topImage}>
        <img src={imageAction} alt="player kicking soccer ball" />
      </div>
      <div className={styles.container}>
        <section>
          <FormHeading isConfirmation heading={formHeading} subheading={<FkoSubHeading />} />
        </section>
        <SessionPreparationContent />
        <section>
          <FormSectionHeading text="Session Details" />
          {players.map((player, index) => (
            <ConfirmationCard key={index} player={player} />
          ))}
          <FormSectionHeading text="Location" />

          <div className={styles.map}>
            {mapSrc && <iframe src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />}
          </div>
          <div className={styles.location}>
            <h3>{mboLocationName}</h3>
            <p>
              {mboLocationDetails.address} {mboLocationDetails.address2}
            </p>
            {mboLocationDetails.phone && <p>{formatPhoneNumber(mboLocationDetails.phone)}</p>}
          </div>
          <SessionExpectationContent />
        </section>
        <section>
          <FkoVideoEmbed />
          <div className={styles.imageWrapper}>
            <img src={imageTraining} alt="player talking with coach" />
          </div>
          <div className="flex flex-col items-center gap-2 p-2 w-full mb-2">
            <FiveStars className="h-[24px] w-[144px] fill-primary" />
            <p className="text-center font-poppins font-medium text-sm text-primary ">
              {`“The interactive sessions have been creative, fun, and greatly beneficial in improving my daughter's game.
            This is already apparent on the playing field, after only a few months of weekly sessions at TOCA.”`}
            </p>
            <p className="text-center font-poppins text-sm text-grey-xdark ">{` - Sara M, Mother of a TOCA Athlete`}</p>
          </div>
          <FkoEndOfPageMessage />
        </section>
      </div>
    </>
  );
};
